const AUXILIARY_VIDEO_CODECS = new Set([
    "rtx",
    "red",
    "ulpfec",
    "flexfec-03",
]);
/**
 * Return the browser encoders that satisfy the camera uplink contract.
 *
 * The bridge intentionally has no transcoder: its inbound camera path accepts
 * H.264 and forwards it to Android. Falling back to another browser default
 * therefore cannot produce a degraded-but-working result; it can only keep the
 * physical camera open while Android receives no frames. Treat H.264 support as
 * a required capability and fail before getUserMedia whenever it is absent.
 */
export function requireH264CodecCapabilities(capabilities) {
    if (!capabilities) {
        throw new Error("browser cannot report video encoder capabilities");
    }
    const h264 = capabilities.codecs.filter((codec) => codec.mimeType.toLowerCase() === "video/h264");
    if (h264.length === 0) {
        throw new Error("browser has no H.264 video encoder for camera sharing");
    }
    return h264;
}
/**
 * Verify that every video m-line shares one H.264-only codec namespace.
 *
 * WebRTC BUNDLE uses a connection-wide payload-type table. Restricting only
 * the later camera transceiver is insufficient: Chrome can retain PT 96 from
 * an earlier VP8 recvonly m-line and then send the webcam with that mapping,
 * even when the camera m-line itself advertises H.264. Enforcing the contract
 * on the initial screen m-line prevents that ambiguous namespace.
 */
export function assertH264VideoBundle(sdp, opts) {
    const mediaSections = sdp
        .replace(/\r\n/g, "\n")
        .split("\nm=")
        .slice(1)
        .map((section) => `m=${section}`);
    const videoSections = mediaSections.filter((section) => {
        const lines = section.split("\n");
        return lines[0]?.startsWith("m=video ");
    });
    if (videoSections.length === 0) {
        throw new Error("offer has no video media section");
    }
    if (opts?.requireSendonlyVideo &&
        !videoSections.some((section) => section.split("\n").includes("a=sendonly"))) {
        throw new Error("camera offer has no sendonly video media section");
    }
    for (const section of videoSections) {
        const lines = section.split("\n");
        const payloadTypes = (lines[0] ?? "").trim().split(/\s+/).slice(3);
        const codecsByPayloadType = new Map();
        for (const line of lines) {
            const match = /^a=rtpmap:(\d+)\s+([^/\s]+)/i.exec(line);
            if (match)
                codecsByPayloadType.set(match[1], match[2].toLowerCase());
        }
        const primaryCodecs = payloadTypes
            .map((payloadType) => codecsByPayloadType.get(payloadType) ?? `unknown:${payloadType}`)
            .filter((codec) => !AUXILIARY_VIDEO_CODECS.has(codec));
        if (primaryCodecs.length === 0 || primaryCodecs.some((codec) => codec !== "h264")) {
            const offered = primaryCodecs.length > 0 ? primaryCodecs.join(", ") : "none";
            throw new Error(`video offer violates H.264-only BUNDLE contract (offered: ${offered})`);
        }
    }
}
export function assertH264SendonlyVideo(sdp) {
    assertH264VideoBundle(sdp, { requireSendonlyVideo: true });
}
